import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, requestJson } from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  name: '',
  emailsText: '',
  ccEmailsText: '',
  followupDelayDays: 5,
  attachmentRequirements: {
    fullFramePhoto: false,
    defectivePartPhoto: false,
    serialNumberRequired: false,
    mandatoryForm: false,
    purchaseInvoice: false,
    defectVideoRequired: false,
  },
  active: true,
};

function splitEmails(value) {
  return [...new Set(String(value || '').split(/[;,\n]/).map((item) => item.trim().toLowerCase()).filter(Boolean))];
}

function joinEmails(values) {
  return (values || []).join('; ');
}

function compareText(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'fr', { numeric: true, sensitivity: 'base' });
}

function attachmentRequirementLabel(requirements) {
  const labels = [];
  if (requirements?.fullFramePhoto) labels.push('Monture');
  if (requirements?.defectivePartPhoto) labels.push('Pièce');
  if (requirements?.serialNumberRequired) labels.push('N° série');
  if (requirements?.mandatoryForm) labels.push('Formulaire');
  if (requirements?.purchaseInvoice) labels.push('Facture');
  if (requirements?.defectVideoRequired) labels.push('Vidéo défaut');
  return labels.length ? labels.join(' + ') : 'Aucune';
}

export default function SuppliersPage() {
  const menuPage = useMenuPage('/fournisseurs');
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/suppliers');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      const haystack = [row.name, ...(row.emails || []), ...(row.ccEmails || []), row.followupDelayDays, attachmentRequirementLabel(row.attachmentRequirements), row.active === false ? 'inactif' : 'actif'].join(' ').toLowerCase();
      return !needle || haystack.includes(needle);
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      const getValue = (row) => {
        if (sortConfig.key === 'emails') return joinEmails(row.emails);
        if (sortConfig.key === 'ccEmails') return joinEmails(row.ccEmails);
        if (sortConfig.key === 'followupDelayDays') return Number(row.followupDelayDays ?? 5);
        if (sortConfig.key === 'attachmentRequirements') return attachmentRequirementLabel(row.attachmentRequirements);
        if (sortConfig.key === 'active') return row.active === false ? '0' : '1';
        return row.name;
      };
      const compare = compareText(getValue(left), getValue(right));
      return sortConfig.direction === 'asc' ? compare : -compare;
    });
    return sorted;
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      name: row.name || '',
      emailsText: joinEmails(row.emails),
      ccEmailsText: joinEmails(row.ccEmails),
      followupDelayDays: row.followupDelayDays ?? 5,
      attachmentRequirements: {
        fullFramePhoto: Boolean(row.attachmentRequirements?.fullFramePhoto),
        defectivePartPhoto: Boolean(row.attachmentRequirements?.defectivePartPhoto),
        serialNumberRequired: Boolean(row.attachmentRequirements?.serialNumberRequired),
        mandatoryForm: Boolean(row.attachmentRequirements?.mandatoryForm),
        purchaseInvoice: Boolean(row.attachmentRequirements?.purchaseInvoice),
        defectVideoRequired: Boolean(row.attachmentRequirements?.defectVideoRequired),
      },
      active: row.active !== false,
    });
    setEditingId(row._id);
    setOpen(true);
  };

  const saveSupplier = async () => {
    if (!form.name.trim()) {
      setError('Saisis un nom fournisseur.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const payload = {
        name: form.name,
        emails: splitEmails(form.emailsText),
        ccEmails: splitEmails(form.ccEmailsText),
        followupDelayDays: Number(form.followupDelayDays),
        attachmentRequirements: form.attachmentRequirements,
        active: form.active,
      };
      const saved = await requestJson(editingId ? `/suppliers/${editingId}` : '/suppliers', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      setRows((current) => {
        const withoutCurrent = current.filter((item) => item._id !== saved._id);
        return [...withoutCurrent, saved].sort((a, b) => compareText(a.name, b.name));
      });
      setOpen(false);
      setMessage(editingId ? 'Fournisseur modifié.' : 'Fournisseur créé.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteSupplier = async () => {
    if (!deleteTarget?._id) return;
    try {
      setSaving(true);
      setError('');
      await requestJson(`/suppliers/${deleteTarget._id}`, { method: 'DELETE' });
      setRows((current) => current.filter((item) => item._id !== deleteTarget._id));
      setDeleteTarget(null);
      setMessage('Fournisseur supprimé.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Fournisseurs
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadRows}>
            Rafraîchir
          </Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            Ajouter
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                <LocalShippingRoundedIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Annuaire fournisseurs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sortedRows.length} fournisseur{sortedRows.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Stack>
              <TextField label="Recherche" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: { xs: '100%', md: 320 } }} />
            </Stack>

            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      ['name', 'Fournisseur'],
                      ['emails', 'Emails destinataires'],
                      ['ccEmails', 'Copies'],
                      ['followupDelayDays', 'Relance après'],
                      ['attachmentRequirements', 'PJ obligatoires'],
                      ['active', 'État'],
                    ].map(([key, label]) => (
                      <TableCell key={key} sx={{ fontWeight: 900 }}>
                        <TableSortLabel active={sortConfig.key === key} direction={sortConfig.key === key ? sortConfig.direction : 'asc'} onClick={() => handleSort(key)}>
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 900 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7}>Chargement...</TableCell>
                    </TableRow>
                  ) : sortedRows.map((row) => (
                    <TableRow key={row._id} hover>
                      <TableCell sx={{ fontWeight: 900 }}>{row.name}</TableCell>
                      <TableCell>{joinEmails(row.emails) || '—'}</TableCell>
                      <TableCell>{joinEmails(row.ccEmails) || '—'}</TableCell>
                      <TableCell>{row.followupDelayDays ?? 5} jour{(row.followupDelayDays ?? 5) > 1 ? 's' : ''}</TableCell>
                      <TableCell>
                        {row.attachmentRequirements?.fullFramePhoto && <Chip size="small" label="Monture" sx={{ mr: 0.5, mb: 0.25 }} />}
                        {row.attachmentRequirements?.defectivePartPhoto && <Chip size="small" label="Pièce" sx={{ mb: 0.25 }} />}
                        {row.attachmentRequirements?.serialNumberRequired && <Chip size="small" label="N° série" sx={{ ml: 0.5, mb: 0.25 }} />}
                        {row.attachmentRequirements?.mandatoryForm && <Chip size="small" label="Formulaire" sx={{ ml: 0.5, mb: 0.25 }} />}
                        {row.attachmentRequirements?.purchaseInvoice && <Chip size="small" label="Facture" sx={{ ml: 0.5, mb: 0.25 }} />}
                        {row.attachmentRequirements?.defectVideoRequired && <Chip size="small" label="Vidéo défaut" sx={{ ml: 0.5, mb: 0.25 }} />}
                        {!row.attachmentRequirements?.fullFramePhoto && !row.attachmentRequirements?.defectivePartPhoto && !row.attachmentRequirements?.serialNumberRequired && !row.attachmentRequirements?.mandatoryForm && !row.attachmentRequirements?.purchaseInvoice && !row.attachmentRequirements?.defectVideoRequired && '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.active === false ? 'Inactif' : 'Actif'} color={row.active === false ? 'default' : 'success'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && !sortedRows.length && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Alert severity="info">Aucun fournisseur trouvé.</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { width: 'min(760px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 24px)', overflowX: 'hidden' } }}
      >
        <DialogTitle>{editingId ? 'Modifier le fournisseur' : 'Créer un fournisseur'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Nom fournisseur" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required fullWidth />
            <TextField
              label="Emails destinataires"
              value={form.emailsText}
              onChange={(event) => setForm((current) => ({ ...current, emailsText: event.target.value }))}
              helperText="Sépare plusieurs emails avec ; ou une nouvelle ligne."
              multiline
              minRows={2}
              fullWidth
            />
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>Pièces jointes obligatoires</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Ces règles s'ajoutent à celles éventuellement définies pour la marque.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 2,
                  rowGap: 0.25,
                  '& .MuiFormControlLabel-root': { m: 0, minWidth: 0, alignItems: 'center' },
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem', lineHeight: 1.25 },
                }}
              >
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.fullFramePhoto} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, fullFramePhoto: event.target.checked } }))} />}
                  label="Photo monture complète"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.defectivePartPhoto} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, defectivePartPhoto: event.target.checked } }))} />}
                  label="Photo pièce défectueuse"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.serialNumberRequired} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, serialNumberRequired: event.target.checked } }))} />}
                  label="Numéro de série obligatoire"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.mandatoryForm} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, mandatoryForm: event.target.checked } }))} />}
                  label="Formulaire obligatoire"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.purchaseInvoice} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, purchaseInvoice: event.target.checked } }))} />}
                  label="Facture d’achat client/fournisseur"
                />
                <FormControlLabel
                  control={<Checkbox checked={form.attachmentRequirements.defectVideoRequired} onChange={(event) => setForm((current) => ({ ...current, attachmentRequirements: { ...current.attachmentRequirements, defectVideoRequired: event.target.checked } }))} />}
                  label="Vidéo du défaut obligatoire"
                />
              </Box>
            </Box>
            <TextField
              label="Emails en copie"
              value={form.ccEmailsText}
              onChange={(event) => setForm((current) => ({ ...current, ccEmailsText: event.target.value }))}
              helperText="Optionnel. Sépare plusieurs emails avec ; ou une nouvelle ligne."
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Délai avant relance"
              type="number"
              value={form.followupDelayDays}
              onChange={(event) => setForm((current) => ({ ...current, followupDelayDays: event.target.value }))}
              helperText="Nombre de jours avant qu'un dossier passe dans les relances fournisseur. 0 = immédiat."
              inputProps={{ min: 0, step: 1 }}
              required
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
              <Typography>{form.active ? 'Fournisseur actif' : 'Fournisseur inactif'}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
          <Button variant="contained" onClick={saveSupplier} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        title="Supprimer ce fournisseur ?"
        description={deleteTarget ? `Le fournisseur ${deleteTarget.name} sera supprimé.` : ''}
        loading={saving}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteSupplier}
      />
    </Stack>
  );
}
